import { FormattedMessage } from 'react-intl';
import { Button, Tag as AntdTag } from 'antd';

import Icon from '../../../components/Icon/Icon';
import Spinner from '../../../components/Spinner/Spinner';

import useArticlesTab from './useArticlesTab';

import { Article, Props, TabStatus } from './ArticlesTab.model';

import styles from './ArticlesTab.module.scss';
import FormPopup from '../../../components/Popup/FormPopup';

const ArticlesTab = ({ i18nCountries, id, triggerArticlePopup }: Props) => {

	const {
		articleFields,
		deleteArticle,
		editArticle,
		setEditArticleForm,
		state: {
			articles,
			editArticleForm,
			showArticleForm,
			tabStatus,
		},
		submitArticle,
		toggleArticleForm
	} = useArticlesTab({triggerArticlePopup});

	const renderChips = (chips: string[]) => {
		return chips.map((field: string, index: number) => (<AntdTag
			key={`${field}${index}`}
			style={{
				border: 'none',
				textTransform: 'uppercase'
			}}>{field}</AntdTag>))
	}

	const renderArticle = (article: Article) => {
		return <div className={styles.articleCard} key={article.articleId}>
			<div className={styles.cardWrapper}>
				<div className={styles.contentWrapper}>
					<div className={styles.icon}>
						<Icon name="file-alt" color="white" width="12px" height="16px" />
					</div>
					<div className={styles.articleContent}>
						<h2>{article.title}</h2>
						<i>{article.authors.map((author: string, index: number) => <span key={`author${index}`}>{author}{index !== article.authors.length - 1 && ', '}</span>)}</i> <br />
						<div>{renderChips(article.tags)}</div>
						<b>{article.publishedIn}, {article.publishingLocation ? `${i18nCountries[article.publishingLocation]},` : ''} {article.publishingYear}</b>
						<p>{article.description}</p>
						<div><a href={article.link} rel="noreferrer" target="_blank"><FormattedMessage id="userLabels.seeArticle" /></a></div>
					</div>
				</div>
				<div className={styles.actions}>
					<Button
						className={`${styles.delete} ${styles.redButtonLink}`}
						onClick={() => deleteArticle(article.articleId)}
						size="large"
						type="link"
					>
						<Icon name="trash" color="red" />
						<FormattedMessage id="buttons.delete" />
					</Button>
					<Button
						className={styles.edit}
						onClick={() => {
							setEditArticleForm(article.articleId);
						}}
						size="large"
						type="default"
					>
						<FormattedMessage id="buttons.edit" />
					</Button>
				</div>
			</div>
		</div>
	}

	return (
		<div className={styles.tabContent}>
			<Spinner spinning={tabStatus === TabStatus.Loading} />
			{tabStatus === TabStatus.Loaded && !articles?.length && <p className={styles.emptyContent}><FormattedMessage id="emptyContent.articles" /></p>}
			{articles.map((article) => renderArticle(article))}
			{editArticleForm && (
				<FormPopup
					actions={[
						{
							onClick: () => {
								setEditArticleForm(undefined);
							}, title: 'cancel', type: 'default'
						},
						{ isSubmitButton: true, title: 'save', type: 'primary' },
					]}
					title={{ action: 'edit', target: 'article' }}
					editId={editArticleForm}
					editData={articles?.find(
						(article) => article.articleId === editArticleForm
					)}
					onSubmitForm={editArticle}
					onCancel={() => {
						setEditArticleForm(undefined);
					}}
					fields={articleFields}
				/>
			)}
			{showArticleForm && (
				<FormPopup
					actions={[
						{ onClick: toggleArticleForm, title: 'cancel', type: 'default' },
						{ isSubmitButton: true, title: 'save', type: 'primary' },
					]}
					title={{ action: 'add', target: 'article' }}
					onSubmitForm={submitArticle}
					onCancel={toggleArticleForm}
					fields={articleFields}
				/>
			)}
		</div>
	);
}

export default ArticlesTab;